import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export type FssaiTagColor = 'GREEN' | 'AMBER' | 'RED' | 'BLUE' | 'PURPLE' | 'GREY';
export type FssaiRuleSource = 'FSSAI' | 'RW';

export interface FssaiGeneratedTag {
  fssaiNutrientTagMasterId: number;
  fssaiTagName?: string;
  operator?: string;
  minValue?: number;
  maxValue?: number;
  nutrientName?: string;
  stateOfFood?: string;
}

export interface FssaiTagDisplayItem {
  fssaiNutrientTagMasterId: number;
  nutrientName: string;
  tagName: string;
  rawValue?: number | null;
  unit?: string;
  formattedValue?: string;
  color: FssaiTagColor;
  colorHex: string;
  badgeClass: string;
  pillClass: string;
  dotClass: string;
  source: FssaiRuleSource;
  sourceLabel: string;
  operator?: string;
  minValue?: number;
  maxValue?: number;
  stateOfFood?: string;
}

export interface FssaiNutrientTagCalculateRequest {
  ingredientId?: number;
  stateOfProduct?: string;
  calories?: number;
  protein?: number;
  fat?: number;
  fiber?: number;
  carbs?: number;
  /** Extra nutrients from label / micronutrient rows (Sugar, Sodium, Vitamin A, …). */
  nutrientValues?: Record<string, number | null | undefined>;
  /** Parallel unit map for nutrientValues (mg, mcg, g, kcal). */
  nutrientUnits?: Record<string, string | null | undefined>;
}

interface ApiResponse<T> {
  success?: boolean;
  Success?: boolean;
  data?: T;
  Data?: T;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class FssaiGeneratedTagService {
  constructor(private http: HttpClient) { }

  calculateTags(request: FssaiNutrientTagCalculateRequest): Observable<ApiResponse<FssaiGeneratedTag[]>> {
    return this.http.post<ApiResponse<FssaiGeneratedTag[]>>(
      environment.baseUrl + 'api/FssaiNutrientTags/calculate',
      request
    );
  }

  getSavedTags(entityType: string, entityId: number): Observable<ApiResponse<FssaiGeneratedTag[]>> {
    return this.http.get<ApiResponse<FssaiGeneratedTag[]>>(
      environment.baseUrl + `api/FssaiNutrientTags/${encodeURIComponent(entityType)}/${entityId}`
    );
  }

  normalizeTags(raw: any): FssaiGeneratedTag[] {
    if (!raw) {
      return [];
    }
    const list = Array.isArray(raw) ? raw : [];
    return list.map((tag: any) => ({
      fssaiNutrientTagMasterId: tag.fssaiNutrientTagMasterId ?? tag.FssaiNutrientTagMasterId ?? 0,
      fssaiTagName: tag.fssaiTagName ?? tag.FssaiTagName ?? tag.tagName ?? tag.TagName ?? tag.name,
      operator: tag.operator ?? tag.Operator,
      minValue: tag.minValue ?? tag.MinValue,
      maxValue: tag.maxValue ?? tag.MaxValue,
      nutrientName: tag.nutrientName ?? tag.NutrientName,
      stateOfFood: tag.stateOfFood ?? tag.StateOfFood,
    })).filter((tag) => !!tag.fssaiTagName);
  }

  extractTagsFromApiResponse(res: any): FssaiGeneratedTag[] {
    if (!res) {
      return [];
    }
    const payload = res.data ?? res.Data ?? res.response ?? res.Response ?? res;
    if (payload?.generatedFssaiNutrientTags) {
      return this.normalizeTags(payload.generatedFssaiNutrientTags);
    }
    if (Array.isArray(payload)) {
      return this.normalizeTags(payload);
    }
    return this.normalizeTags(payload?.generatedFssaiNutrientTags);
  }

  /**
   * Centralized color classification mapping for FSSAI and ReeWork nutrient tags.
   *
   * GREEN: Positive / favourable direction (more useful OR less useful).
   * AMBER: Source / watch / intermediate claims.
   * RED: Limit / attention (high levels of nutrients that should generally be limited).
   * BLUE: Personalise / context dependent.
   * PURPLE: Special characteristic (Low GI, probiotic, lactose-free, functional attribute).
   * GREY: Information only / fallback / unknown / no claim.
   */
  getTagColor(tagName?: string, nutrientName?: string): FssaiTagColor {
    const name = String(tagName || '').trim().toLowerCase();

    if (!name) {
      return 'GREY';
    }

    // 1. PURPLE: Special characteristic (Low GI, probiotic, lactose-free, functional attribute)
    if (
      name.includes('low gi') ||
      name.includes('glycemic index') ||
      name.includes('glycaemic index') ||
      name.includes('probiotic') ||
      name.includes('lactose free') ||
      name.includes('free lactose')
    ) {
      return 'PURPLE';
    }

    // 2. RED: Limit / attention. High levels of nutrients that should generally be limited.
    if (
      name.includes('high calorie') ||
      name.includes('high energy') ||
      name.includes('high calories') ||
      name.includes('high cholesterol') ||
      name.includes('high sodium') ||
      name.includes('high salt') ||
      name.includes('high saturated fat') ||
      name.includes('high trans fat') ||
      name.includes('high sugar') ||
      name.includes('high added sugar') ||
      name.includes('high chloride')
    ) {
      return 'RED';
    }

    // 3. BLUE: Personalise / context dependent.
    if (
      name.includes('other/context') ||
      name.includes('context carbohydrate') ||
      name.includes('context carb') ||
      name.includes('personalise')
    ) {
      return 'BLUE';
    }

    // 4. AMBER: Source / watch / intermediate claims.
    // Explicit source claims (Source Calcium, Source Zinc, Source Fibre, Source Vitamins, Source Minerals, Source Omega 3, Source Protein)
    if (
      name.startsWith('source ') ||
      name.includes('source')
    ) {
      return 'AMBER';
    }

    // 5. GREEN: Positive / favourable direction.
    // - Positive High: High Protein, High Fibre/Dietary Fibre, High Unsaturated Fat, High MUFA/PUFA, High Omega 3, High Vitamins, High Minerals
    if (
      name.startsWith('high ') ||
      name.includes('high/rich') ||
      name.includes('high-rich') ||
      name.startsWith('rich ')
    ) {
      return 'GREEN';
    }

    // - Favourable Low / Free (Limit nutrients where low is useful):
    //   Low/Free Saturated Fat, Low/Free Total Fat, Free Trans Fat, Low/Free Energy or Calorie,
    //   Low/Free Sodium, Low/Free Sugar or Added Sugar, Low/Free Cholesterol, Low Carbohydrate
    if (
      name.includes('saturated fat') ||
      name.includes('total fat') ||
      name.includes('trans fat') ||
      name.includes('energy') ||
      name.includes('calorie') ||
      name.includes('sodium') ||
      name.includes('sugar') ||
      name.includes('cholesterol') ||
      name.includes('carbohydrate') ||
      name.includes('carb')
    ) {
      if (
        name.startsWith('low ') ||
        name.startsWith('very low ') ||
        name.startsWith('free ') ||
        name.includes(' free')
      ) {
        return 'GREEN';
      }
    }

    // 6. GREY: Information only / neutral classification / no defined regulatory color claim in source document
    // (e.g. Low Vitamin E, Low Vitamin K, Low Vitamin B1-B9, Low Potassium, Low Chromium, Low Protein, Low Dietary Fibre)
    return 'GREY';
  }

  /**
   * Distinguishes FSSAI regulatory rules from ReeWork internal rules.
   */
  getTagSource(tagName?: string, nutrientName?: string): FssaiRuleSource {
    const name = String(tagName || '').trim().toLowerCase();

    // High Calorie & Low/Context Carbohydrate are ReeWork internal rules
    if (
      name.includes('high calorie') ||
      name.includes('high calories') ||
      name.includes('carbohydrate') ||
      name.includes('carb') ||
      name.includes('context')
    ) {
      return 'RW';
    }

    return 'FSSAI';
  }

  getTagColorHex(color: FssaiTagColor): string {
    switch (color) {
      case 'GREEN':
        return '#10b981';
      case 'AMBER':
        return '#f59e0b';
      case 'RED':
        return '#ef4444';
      case 'BLUE':
        return '#3b82f6';
      case 'PURPLE':
        return '#8b5cf6';
      case 'GREY':
      default:
        return '#6b7280';
    }
  }

  getTagBadgeClass(color: FssaiTagColor): string {
    return `fssai-badge--${color.toLowerCase()}`;
  }

  /**
   * Extracts clean, human-readable nutrient name from tag and metadata.
   */
  resolveNutrientDisplayName(tag: FssaiGeneratedTag): string {
    if (tag.nutrientName && tag.nutrientName.trim()) {
      return tag.nutrientName.trim();
    }
    const name = String(tag.fssaiTagName || '').trim();
    const clean = name
      .replace(/^(Low|Very Low|Free|High|High\/Rich|High-Rich|Rich|Source)\s+/i, '')
      .replace(/(\s*-\s*fatty acids.*)/i, '')
      .trim();

    return clean || 'Nutrient';
  }

  /**
   * Enriches raw FSSAI generated tag into display item with color, source, classes, and formatted value.
   */
  enrichTag(
    tag: FssaiGeneratedTag,
    nutrientValue?: number | string | null,
    nutrientUnit?: string | null
  ): FssaiTagDisplayItem {
    const nutrientName = this.resolveNutrientDisplayName(tag);
    const tagName = (tag.fssaiTagName || '').trim();
    const color = this.getTagColor(tagName, nutrientName);
    const source = this.getTagSource(tagName, nutrientName);

    let rawValNum: number | null = null;
    let formattedValue = '';

    if (nutrientValue !== null && nutrientValue !== undefined && String(nutrientValue).trim() !== '') {
      const n = Number(nutrientValue);
      if (Number.isFinite(n)) {
        rawValNum = n;
        const unitStr = String(nutrientUnit || '').trim();
        const formattedNum = Number.isInteger(n) ? n.toString() : parseFloat(n.toFixed(3)).toString();
        formattedValue = unitStr ? `${formattedNum} ${unitStr}` : `${formattedNum}`;
      }
    }

    if (!formattedValue) {
      const unitStr = String(nutrientUnit || '').trim();
      formattedValue = unitStr ? `0 ${unitStr}` : `0`;
      rawValNum = 0;
    }

    return {
      fssaiNutrientTagMasterId: tag.fssaiNutrientTagMasterId || 0,
      nutrientName,
      tagName,
      rawValue: rawValNum,
      unit: nutrientUnit || undefined,
      formattedValue: formattedValue || undefined,
      color,
      colorHex: this.getTagColorHex(color),
      badgeClass: `fssai-tag-badge--${color.toLowerCase()}`,
      pillClass: `fssai-colour-pill--${color.toLowerCase()}`,
      dotClass: `fssai-colour-dot--${color.toLowerCase()}`,
      source,
      sourceLabel: source === 'FSSAI' ? 'FSSAI' : 'ReeWork Internal (RW)',
      operator: tag.operator,
      minValue: tag.minValue,
      maxValue: tag.maxValue,
      stateOfFood: tag.stateOfFood,
    };
  }
}
